import type {
  DefaultEcoLifeField,
  EcoLife,
  ProductColor,
  SubjectiveCharacteristic,
} from '@aloft/toolkit-shared';
import { isDefined, noop } from '@aloft/toolkit-shared';
import {
  Flex,
  IconButton,
  InfoIcon,
  SimpleInput,
  SimpleRange,
  Tooltip,
  Typography,
} from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { Popover, PopoverContent, PopoverTrigger, Portal } from '@aloft/toolkit-sui/chakra';

import { GuidedTourTooltip } from '../../../../components/GuidedTour/GuidedTourTooltip.tsx';
import { setTotalEconomicLife } from '../../../../redux/features/actions/setTotalEconomicLife.ts';
import { sliderAdjustment } from '../../../../redux/features/actions/sliderAdjustment.ts';
import { selectIsInspectifyOrder } from '../../../../redux/features/selectors/orderSelectors.ts';
import {
  selectEcoLifeByIdAndField,
  selectImprovementsById,
  selectPropertyTypeById,
  selectSubjectiveCharacteristicsByIdAndField,
} from '../../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { selectFirstOrFewerCompID } from '../../../../redux/features/selectors/selectFirstOrFewerCompID.ts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.ts';
import type { RootState } from '../../../../redux/types.ts';
import { clearFocusedCostApproachSliders } from '../../../alerts/costApproachSliderFocus.ts';
import { useCostApproachSliderAlertHighlight } from '../../../alerts/useCostApproachSliderAlertHighlight.ts';
import { propertyTypesWithImageAnalysis } from '../../consts.ts';
import { ImageAnalysisEffectiveAge } from '../ImageAnalysis/ImageAnalysisEffectiveAge.tsx';

const MIN = 0;
const EXCEED_ACTUAL_AGE_TOOLTIP =
  'Effective Age typically does not exceed Actual Age. If the property shows above-average wear or obsolescence, consider updating the Condition rating.';

type InputsProps = {
  id: string;
  index: number;
  effectiveAgeField: SubjectiveCharacteristic;
  ecoLifeField: EcoLife;
  defaultEcoLifeField: DefaultEcoLifeField;
  label?: string;
  labelColor?: ProductColor;
  enabled: boolean;
};

export const EffectiveAgeSliderInputs = ({
  id,
  index,
  effectiveAgeField,
  ecoLifeField,
  defaultEcoLifeField,
  label,
  labelColor,
  enabled,
}: InputsProps) => {
  const dispatch = useAppDispatch();
  const effectiveAgeValue =
    useAppSelector((state: RootState) =>
      selectSubjectiveCharacteristicsByIdAndField(state, id, effectiveAgeField),
    ) ?? 0;
  const actualAge = useAppSelector((state: RootState) => selectImprovementsById(state, id)?.age);
  const secondOrFewerCompID = useAppSelector(selectFirstOrFewerCompID);
  const inspectifyOrder = useAppSelector(selectIsInspectifyOrder);
  // Ignores sliders like Garage Age, Porch Age, etc.
  const isMainEffectiveAge = label === 'Effective Age';
  const { containerRef, highlightSx } = useCostApproachSliderAlertHighlight(
    isMainEffectiveAge ? id : '',
  );

  const totalEconomicLifeValue =
    useAppSelector((state: RootState) =>
      selectEcoLifeByIdAndField(state, id, ecoLifeField, defaultEcoLifeField),
    ) ?? 0;
  const propertyType = useAppSelector((state: RootState) => selectPropertyTypeById(state, id));
  const maxSliderEffectiveAge = isDefined(actualAge)
    ? Math.min(totalEconomicLifeValue, actualAge)
    : totalEconomicLifeValue;
  const exceedsActualAge = isDefined(actualAge) && effectiveAgeValue > actualAge;

  const handleEffectiveAgeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isMainEffectiveAge) {
      clearFocusedCostApproachSliders(dispatch);
    }
    dispatch(
      sliderAdjustment({
        id,
        field: effectiveAgeField,
        newValue: Number(event.target.value),
      }),
    );
  };

  const handleTotalEconomicLifeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isMainEffectiveAge) {
      clearFocusedCostApproachSliders(dispatch);
    }
    dispatch(
      setTotalEconomicLife({ id, field: ecoLifeField, newValue: Number(event.target.value) }),
    );
  };

  return (
    <Flex
      ref={isMainEffectiveAge ? containerRef : undefined}
      flexDirection="column"
      __dangerDoNotUseSx={isMainEffectiveAge ? highlightSx : undefined}
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Flex alignItems="center" gap="4px">
          <Typography variant="h4" color={labelColor ?? 'textSecondary'}>
            {label ?? ''}
          </Typography>
          {label === 'Effective Age' &&
            propertyTypesWithImageAnalysis.includes(propertyType ?? '') && (
              <ImageAnalysisEffectiveAge id={id} />
            )}
          {/* TODO: AV-729 - Add AI analysis icon */}
          {inspectifyOrder && (
            <InfoIcon tooltipContent="Major itemized repair costs are handled separately in 'Total Cost to Cure' on the Sales Comparison page, and should not influence this rating." />
          )}
        </Flex>
        <Flex gap=".5rem" alignItems="center">
          <Typography variant="subtitle3" color="textSecondary">
            Econ. Life {totalEconomicLifeValue.toString()}
          </Typography>
          <Popover placement="top">
            {({ onClose, isOpen }) => (
              <>
                <PopoverTrigger>
                  <IconButton
                    size="xs"
                    ariaLabel="Set Total Economic Life"
                    icon="pencil"
                    variant="tertiary"
                    iconColor="textSecondary"
                    onClick={isOpen ? onClose : noop}
                  />
                </PopoverTrigger>
                <Portal>
                  <PopoverContent width="fit-content" zIndex={3}>
                    <Flex
                      gap="1rem"
                      width="100%"
                      justifyContent="flex-start"
                      alignItems="center"
                      padding="1rem"
                    >
                      <SimpleInput
                        label="Total Economic Life"
                        type="number"
                        width="10rem"
                        value={totalEconomicLifeValue?.toString()}
                        onBlur={handleTotalEconomicLifeChange}
                        min={1}
                      />
                    </Flex>
                  </PopoverContent>
                </Portal>
              </>
            )}
          </Popover>
        </Flex>
      </Flex>
      <GuidedTourTooltip
        text="Then use the actual age and photos of the property to estimate the effective age of each property."
        tourID="analysisTour"
        tourIndex={5}
        displayCondition={id === secondOrFewerCompID && index === 0}
        placement="top"
      >
        <Flex gap="1rem" width="100%" justifyContent="space-between" alignItems="center">
          <SimpleRange
            value={Math.min(effectiveAgeValue, maxSliderEffectiveAge).toString()}
            min={MIN}
            max={maxSliderEffectiveAge}
            onChange={handleEffectiveAgeChange}
            disabled={!enabled}
          />

          <Tooltip
            label={EXCEED_ACTUAL_AGE_TOOLTIP}
            isOpen={exceedsActualAge}
            placement="bottom"
            hasArrow
            maxWidth="20rem"
          >
            <SimpleInput
              type="number"
              min={MIN}
              max={totalEconomicLifeValue}
              value={effectiveAgeValue.toString()}
              onChange={handleEffectiveAgeChange}
              size="sm"
              width="4rem"
              disabled={!enabled}
            />
          </Tooltip>
        </Flex>
      </GuidedTourTooltip>
    </Flex>
  );
};
